<template>
  <div class="cat-grid-wrap">
    <div v-if="loading" class="cat-loading">Loading categories...</div>
    <div v-else class="cat-cards">
      <div
        v-for="cat in sortedData"
        :key="cat.name"
        class="cat-card"
        :class="{ 'cat-card--selected': selected?.name === cat.name }"
        @click="toggle(cat)"
      >
        <div class="cat-card-top">
          <i :class="['fas', iconFor(cat.name), 'cat-icon']"></i>
          <span class="cat-name">{{ cat.display_name }}</span>
          <span class="cat-spent" :class="isOver(cat) ? 'val-over' : ''">
            ${{ fmt(cat.spent) }}
          </span>
          <span
            v-if="trendLabel(cat)"
            class="cat-trend"
            :class="trendClass(cat)"
            :title="'vs last month: ' + trendLabel(cat)"
          >{{ trendLabel(cat) }}</span>
        </div>
        <template v-if="cat.budgeted > 0">
          <div class="cat-bar-bg">
            <div
              class="cat-bar-fill"
              :class="isOver(cat) ? 'cat-bar--over' : 'cat-bar--ok'"
              :style="{ width: pct(cat) + '%' }"
            />
          </div>
          <div class="cat-card-bot">
            <span class="cat-of">of ${{ fmt(cat.budgeted) }}</span>
            <span :class="isOver(cat) ? 'val-over' : 'val-ok'">
              ${{ fmt(Math.abs((cat.budgeted || 0) - (cat.spent || 0))) }}
              {{ isOver(cat) ? 'over' : 'left' }}
            </span>
          </div>
        </template>
      </div>
    </div>
    <div v-if="sortedData.length > 0" class="cat-totals">
      <span>Total spent: <strong>${{ fmt(totalSpent) }}</strong></span>
      <span>Budget: <strong>${{ fmt(totalBudget) }}</strong></span>
    </div>
  </div>
</template>

<script>
const ICONS = {
  gym_memberships: 'fa-dumbbell',
  rent: 'fa-home',
  daily_travel: 'fa-subway',
  health_and_fitness: 'fa-heartbeat',
  car_payment: 'fa-car',
  utilities: 'fa-lightbulb',
  steep_hollow: 'fa-tree',
  subscriptions: 'fa-podcast',
  zogchow: 'fa-utensils',
  car_insurance: 'fa-shield-alt',
  clothing: 'fa-tshirt',
  discretionary: 'fa-store',
  car_maintenance: 'fa-wrench',
  other: 'fa-folder',
  entertainment: 'fa-mountain',
  groceries: 'fa-carrot',
};

export default {
  name: 'CategoryGrid',
  props: { data: Array, loading: Boolean, prev_spending: Object },
  emits: ['category-selected'],
  data() {
    return { selected: null };
  },
  computed: {
    sortedData() {
      if (!this.data) return [];
      return [...this.data].sort((a, b) => {
        if (a.name === 'other') return 1;
        if (b.name === 'other') return -1;
        return (b.budgeted || 0) - (a.budgeted || 0);
      });
    },
    totalSpent() {
      return (this.data || []).reduce((s, c) => s + (c.spent || 0), 0);
    },
    totalBudget() {
      return (this.data || []).reduce((s, c) => s + (c.budgeted || 0), 0);
    },
  },
  methods: {
    iconFor(name) { return ICONS[name] || 'fa-tag'; },
    isOver(cat) { return (cat.budgeted || 0) > 0 && (cat.spent || 0) > cat.budgeted; },
    pct(cat) { return Math.min(100, ((cat.spent || 0) / cat.budgeted) * 100); },
    fmt(val) { return Math.round(val || 0).toLocaleString(); },
    trendPct(cat) {
      const prev = this.prev_spending?.[cat.name];
      if (prev == null || prev === 0) return null;
      const curr = cat.spent || 0;
      return Math.round(((curr - prev) / prev) * 100);
    },
    trendLabel(cat) {
      const p = this.trendPct(cat);
      if (p === null) return null;
      return (p > 0 ? '+' : '') + p + '%';
    },
    trendClass(cat) {
      const p = this.trendPct(cat);
      if (p === null) return null;
      if (p >  10) return 'trend-up';   // spending more = bad
      if (p < -10) return 'trend-down'; // spending less = good
      return 'trend-flat';
    },
    toggle(cat) {
      if (this.selected?.name === cat.name) {
        this.selected = null;
        this.$emit('category-selected', null);
      } else {
        this.selected = cat;
        this.$emit('category-selected', cat);
      }
    },
  },
  watch: {
    data(newData) {
      if (this.selected && newData) {
        this.selected = newData.find(c => c.name === this.selected.name) || null;
      }
    },
  },
};
</script>

<style scoped>
.cat-grid-wrap {
  padding: 8px 12px 4px;
}
.cat-loading {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
}
.cat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  padding-bottom: 4px;
}
.cat-card {
  background: #fff;
  border: 1.5px solid #e8eaf0;
  border-radius: 12px;
  padding: 10px 12px 8px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  user-select: none;
}
.cat-card:hover {
  border-color: #4f8ef7;
  box-shadow: 0 2px 8px rgba(79, 142, 247, 0.15);
}
.cat-card--selected {
  border-color: #4f8ef7;
  background: #f0f5ff;
}
.cat-card-top {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 6px;
}
.cat-icon {
  color: #6b7280;
  font-size: 13px;
  flex-shrink: 0;
  width: 16px;
  text-align: center;
}
.cat-name {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cat-spent {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}
.cat-bar-bg {
  height: 5px;
  background: #e8eaf0;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 5px;
}
.cat-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.cat-bar--ok { background: #10b981; }
.cat-bar--over { background: #ef4444; }
.cat-card-bot {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
}
.cat-of { color: #9ca3af; }
.val-ok { color: #10b981; font-weight: 600; }
.val-over { color: #ef4444; font-weight: 600; }
/* Trend badges */
.cat-trend {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 99px;
  flex-shrink: 0;
  margin-left: 2px;
}
.trend-up   { background: #fee2e2; color: #dc2626; }
.trend-down { background: #d1fae5; color: #059669; }
.trend-flat { background: #f3f4f6; color: #6b7280; }

.cat-totals {
  display: flex;
  gap: 20px;
  padding: 8px 4px 2px;
  font-size: 13px;
  color: #6b7280;
  border-top: 1px solid #f1f2f6;
  margin-top: 6px;
}
</style>
